import React, { createContext, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { readStoredUser } from "../../utils/sessionUser";
import useOcrBusinessUnitScope from "../hooks/useOcrBusinessUnitScope";
import useOcrUploadQueue from "../hooks/useOcrUploadQueue";

import useInvoiceBackgroundUploads from "../hooks/useInvoiceBackgroundUploads";

const OcrBackgroundUploads = createContext(null);
export const useOcrBackgroundUploads = () => useContext(OcrBackgroundUploads);

export function hasOcrPermission(user, action) {
  return Number(user?.id) === 1 || user?.permissions?.access_settings === true ||
    user?.permissions?.[`purchasing.delivery_notes.${action}`] === true;
}

const canUpload = user => hasOcrPermission(user, "view_tab") && hasOcrPermission(user, "create");

function ScopedUploads({ children }) {
  // This scope belongs to the purchasing workspace, not an individual popup.
  const scope = useOcrBusinessUnitScope(true);
  const { api, blocked, user } = scope;
  const invoiceUploads = useInvoiceBackgroundUploads(scope);
  const [revision, setRevision] = useState(0);
  const queue = useOcrUploadQueue({
    api, enabled: !blocked && canUpload(user),
    canContinue: () => canUpload(readStoredUser()),
    onRefresh: () => setRevision(value => value + 1),
  });
  return <OcrBackgroundUploads.Provider value={{ queue, revision, invoiceUploads }}>{children}</OcrBackgroundUploads.Provider>;
}

export default function OcrBackgroundUploadsProvider({ children }) {
  const [user, setUser] = useState(readStoredUser);
  const [query] = useSearchParams();
  useEffect(() => {
    const refresh = () => setUser(readStoredUser());
    window.addEventListener("storage", refresh);
    window.addEventListener("palantir-session-updated", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("palantir-session-updated", refresh);
    };
  }, []);
  // A different session/BU gets a fresh queue. Late responses from the old
  // scope cannot start OCR or send any of its remaining files to the new BU.
  const routeBu = query.has("business_unit_id") ? Number(query.get("business_unit_id")) : Number(user?.active_business_unit_id);
  const scope = JSON.stringify([String(user?.id || ""), Number(user?.active_business_unit_id) || null, routeBu]);
  return <ScopedUploads key={scope}>{children}</ScopedUploads>;
}
